import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useExpanded, useTable } from "react-table";
import Icon from "../../../../assets/icons";
import { formatNumberWithCommas, truncateString } from "../../../../helpers";
import primaryComponents from "../../../primaryComponents";
import secondaryComponents from "../..";
import styles from "./styles.module.scss";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { pathConstants } from "../../../../routes/pathContants";
import { UpDownArrow } from "../../../../assets/svgIcons";
import { useUrlSearch } from "../../../../hooks/useUrlSearch";
import { PricingTooltip } from "../ServiceTable/PricingTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ProductTableActions from "./ProductTableActions";
import { useGetProductDetails } from "../../../../hooks/api/queries";
import { useProductSelect } from "../../../../hooks/useProductSelect";

export const useProductTable = ({
  productList = [],
  formData = {},
  setFormData = () => {},
}) => {
  const { search, filterByKey } = useUrlSearch();
  const navigate = useNavigate();
  const [views, setViews] = useState({
    isDelete: null,
    isRestock: null,
    isBarcode: null,
  });
  const { handleCheckBoxUpdate, handleCheckAll } = useProductSelect({
    formData,
    setFormData,
  });

  const columns = useMemo(
    () => [
      {
        Header: (
          <primaryComponents.Checkbox
            name="selectAllProducts"
            id="selectAllProducts"
            labelClassName={styles.selectAll}
            isChecked={formData?.is_select_all}
            onChange={() => handleCheckAll(productList)}
          >
            <span className="d-block mt-1 smallest-text-size gray--text">
              Proudct ID
            </span>
          </primaryComponents.Checkbox>
        ),
        accessor: "id",
        Cell: ({ cell: { value }, row }) => {
          if (row?.original?.variant_id) return <span>{value}</span>;

          const idKey = row?.original?.is_composite
            ? `${row?.original?.composite_product_id}`
            : `${row?.original?.id}`;

          return (
            <primaryComponents.Checkbox
              name="checkedProduct"
              id={idKey}
              isChecked={
                formData?.selected_ids?.includes(idKey) ||
                (formData?.is_select_all &&
                  !formData?.exclude_selected_ids?.includes(idKey))
              }
              onChange={() => {
                handleCheckBoxUpdate(idKey);
              }}
            >
              <span className="d-block mt-1 smallest-text-size gray--text">
                {value}
              </span>
            </primaryComponents.Checkbox>
          );
        },
      },

      {
        Header: (
          <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>Name</span>
            <secondaryComponents.ToolTipV2
              tipText={
                search.get("order_by") === "name"
                  ? "Sort by descending order"
                  : search.get("order_by") === "-name"
                    ? "Sort by ascending order"
                    : "Sort by ascending order"
              }
              position="right"
            >
              <span
                className="cursor-pointer"
                onClick={() =>
                  filterByKey(
                    "order_by",
                    search.get("order_by") === "name" ? "-name" : "name",
                  )
                }
              >
                <UpDownArrow />
              </span>
            </secondaryComponents.ToolTipV2>
          </span>
        ),
        accessor: "name",
        Cell: ({ cell: { value } }) => truncateString(value, 200),
      },

      {
        Header: "Product Type",
        accessor: (originalRow) => {
          if (originalRow?.variant_id)
            return `${originalRow?.option}-${originalRow?.value}`;
        },
        Cell: ({ cell: { value }, row }) => {
          return (
            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              {row?.depth !== 1 ? (
                <span
                  style={{
                    background: "#EFEFEF",
                    padding: "3px 8px",
                    borderRadius: "8px",
                  }}
                  className="gray--text"
                >
                  {row?.original?.variants?.length > 0
                    ? "Variant"
                    : row?.original?.composite_product_type === "ON_DEMAND"
                      ? "On Demand"
                      : row?.original?.composite_product_type === "STOCKED"
                        ? "Stocked"
                        : "Single Product"}
                </span>
              ) : (
                <span>{`${value}` || "N/A"}</span>
              )}
              {row?.canExpand && (
                <span
                  {...row?.getToggleRowExpandedProps({
                    style: {
                      // We can even use the row.depth property
                      // and paddingLeft to indicate the depth
                      // of the row
                      // paddingRight: `${row.depth * 2}rem`,
                    },
                  })}
                >
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#E4DAFC",
                      borderRadius: "2px",
                      border: "1px solid #7647ee",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={row?.isExpanded ? "angle-up" : "angle-down"}
                      color="#7647ee"
                    />
                  </span>
                </span>
              )}
            </div>
          );
        },
      },

      {
        Header: "Selling Price",
        Cell: ({ cell: { row } }) => {
          if (row?.canExpand) return <img src={Icon.variantCollapse} alt="" />;

          return (
            <PricingTooltip
              name={row?.original?.name}
              pricings={row?.original.pricings}
              store_currency={row?.original.store_currency}
            />
          );
        },
      },
      {
        Header: "Stock Quantity",
        accessor: "stock_quantity",
        Cell: ({ cell: { value, row } }) => {
          if (value !== null && value !== undefined)
            return (
              <span
                className={`${row.original["minimum_stock_quantity"] >= value ? globalStyles["statusBlock--failed"] : globalStyles["statusBlock--success"]}`}
                style={{ minWidth: 0 }}
              >
                {formatNumberWithCommas(value)}
              </span>
            );

          if (row?.canExpand && !value)
            return <img src={Icon.variantCollapse} alt="" />;

          if (!row?.canExpand && !value) return "N/A";
        },
      },
      {
        Header: "Unit",
        accessor: "unit",
        Cell: ({ cell: { value } }) => {
          return <span>{value ? value?.unit : "N/A"}</span>;
        },
      },
      {
        Header: "Actions",
        Cell: ({ cell: { row } }) => {
          const is_composite = row?.original?.is_composite;

          return row?.depth === 1 ? null : (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <secondaryComponents.NavigateWithPermission
                permissionParentKey={"Inventory Management"}
                permissionChildKey="can_restock_products_bulk_and_single_restock"
              >
                <primaryComponents.Button
                  onClick={() => {
                    setViews((prev) => ({
                      ...prev,
                      isRestock: row?.original?.id,
                    }));
                  }}
                  classNames={"btn btn--primary smallBtn"}
                  style={{ width: "100px" }}
                  isDisabled={
                    row?.original?.composite_product_type === "ON_DEMAND"
                  }
                >
                  {is_composite ? "Produce" : "Add Stock"}
                </primaryComponents.Button>
              </secondaryComponents.NavigateWithPermission>

              <secondaryComponents.NavigateWithPermission
                permissionParentKey={"Inventory Management"}
                permissionChildKey="can_edit_or_update_product"
              >
                <primaryComponents.Button
                  classNames={"btn btn--outline smallBtn"}
                  onClick={() => {
                    if (is_composite)
                      return navigate(
                        pathConstants.INVENTORY_PRODUCTS_EDIT_COMPOSITE({
                          id: row.original["composite_product_id"],
                        }),
                      );

                    navigate(
                      pathConstants.INVENTORY_PRODUCT_EDIT({
                        id: row.original["id"],
                      }),
                    );
                  }}
                >
                  Update
                </primaryComponents.Button>
              </secondaryComponents.NavigateWithPermission>

              <secondaryComponents.ToolTipV2
                tipText="View Barcode"
                position="left"
              >
                <primaryComponents.Button
                  onClick={() => {
                    setViews((prev) => ({
                      ...prev,
                      isBarcode: row?.original?.id,
                    }));
                  }}
                  classNames={"btn btn--outline smallBtn"}
                >
                  <img src={Icon.barCodeCreateIcon} alt="" />
                </primaryComponents.Button>
              </secondaryComponents.ToolTipV2>

              <ProductTableActions
                product={row?.original}
                views={views}
                setViews={setViews}
              />
            </span>
          );
        },
      },
    ],
    [search.get("order_by"), formData],
  );

  const instance = useTable(
    {
      columns,
      data: productList,
    },
    useExpanded,
  );

  const { isLoading: isFetchingProductDetails, data: selectedProduct } =
    useGetProductDetails({ id: views?.isBarcode || views?.isRestock });

  return {
    instance,
    selectedProduct,
    isFetchingProductDetails,
    views,
    setViews,
  };
};
